// PN1 / DC-4 — THE SERVICE WORKER'S LAW, AND IT SHIPS BEFORE ANY PUSH DOES.
//
// Her order is exact: **the four update-strategy pieces ship in the SAME COMMIT as the worker, or
// the worker does not ship**, and **the worker must never cache `/api/*`**. This file is that law,
// imported by the page (which registers the worker and raises the affordance) and RUN by
// `scripts/pn1-service-worker.mjs`. `public/sw.js` cannot import it — a service worker that used ES
// modules would not run on the iOS Safari her members are on — so the worker carries its own copy of
// the decisions and **the fixture drives BOTH over the same table of URLs and compares the answers.**
// Two implementations with one answer key, held to each other across the boundary, rather than two
// answer keys nobody notices drifting.
//
// WHY A SERVICE WORKER IS DANGEROUS ENOUGH TO NEED THIS MUCH LAW: a worker that caches wrongly does
// not fail loudly. It serves a member yesterday's app forever, on a device nobody can reach, and no
// deploy fixes it. Every rule below exists to make that outcome impossible rather than unlikely.
//
// Imports NOTHING (the entry requirement for the fixture to load and run it).

// The cache name carries a VERSION, and `activate` deletes every cache that is not this one. That
// single line is what makes a bad cache a one-deploy problem instead of a permanent one.
export const CACHE_VERSION = "v1";
export const CACHE_NAME = `12h-shell-${CACHE_VERSION}`;

// The page asks the waiting worker to take over with this message, and the worker listens for
// exactly this string. The fixture holds the two to each other.
export const SKIP_WAITING = "12H_SKIP_WAITING";

// ---------------------------------------------------------------------------
// WHAT MAY BE CACHED. **NEVER `/api/*`** — her rule, and it is the first thing checked, before
// anything else can grant a URL a cache entry.
//
// The three that follow it are the same rule for the same reason: a member's answer is not a static
// asset. A cached reading, a cached session or a cached seal image is one member's private content
// sitting in a store the next request can read out of.
// ---------------------------------------------------------------------------
export function mayCache(url, method = "GET") {
  if (String(method).toUpperCase() !== "GET") return false;
  let path = String(url || "");
  let origin = "";
  try {
    const u = new URL(path, "http://x");
    path = u.pathname;
    origin = u.origin;
  } catch { return false; }
  // Cross-origin is somebody else's cache policy to set, not ours.
  if (origin && origin !== "http://x" && !String(url).startsWith("/")) return false;
  if (path.startsWith("/api/")) return false;          // HER RULE, FIRST.
  if (path.startsWith("/auth/")) return false;         // sessions are never a static asset
  if (path === "/sw.js") return false;                 // a cached worker cannot be replaced
  return true;
}

// HOW A REQUEST IS SERVED.
//
//   network-first  — HTML. The document names the asset bundle, so a stale document is a stale APP.
//                    The cache is the offline fallback and nothing more.
//   cache-first    — /assets/*, which Vite fingerprints with a content hash. A hashed file never
//                    changes under its name, so this is safe by construction and is the only place
//                    it is.
//   network-only   — everything else, including every API call, which is therefore never touched.
export function strategyFor(url, { navigate = false, method = "GET" } = {}) {
  if (!mayCache(url, method)) return "network-only";
  if (navigate) return "network-first";
  let path = String(url || "");
  try { path = new URL(path, "http://x").pathname; } catch { /* keep the raw string */ }
  // Vite fingerprints as `name-HASH.ext` (a DASH, not a dot — the first version of this pattern
  // looked for `name.HASH.ext` and therefore matched nothing this site ships, which is the quiet
  // way a cache layer ends up doing nothing at all).
  if (/^\/assets\/.+-[A-Za-z0-9_-]{8,}\.(js|css|woff2?|png|jpe?g|webp|svg)$/.test(path)) return "cache-first";
  if (/^\/(icon-\d+\.png|apple-touch-icon\.png|favicon[^/]*|site\.webmanifest)$/.test(path)) return "cache-first";
  return "network-only";
}

// ---------------------------------------------------------------------------
// WHEN THE PAGE ASKS THE BROWSER TO LOOK FOR A NEW WORKER (her third piece).
//
// A registration only checks for an update on its own schedule (roughly daily), which on a site
// somebody keeps open on a phone means a fix can sit undelivered for a day. These are the two moments
// that cost nothing and catch everything: she moved to another room, or she came back to the tab.
// ---------------------------------------------------------------------------
export const UPDATE_REASONS = ["navigation", "foreground", "manual"];
export function shouldCheckForUpdate(reason) {
  return UPDATE_REASONS.includes(String(reason || ""));
}
// A floor between checks, so a burst of navigation does not become a burst of requests. Generous:
// the check is one conditional GET of a small file.
export const UPDATE_CHECK_FLOOR_MS = 60_000;
export function mayCheckNow(reason, lastCheckMs, nowMs) {
  if (!shouldCheckForUpdate(reason)) return false;
  if (reason === "manual") return true;
  return (Number(nowMs) - Number(lastCheckMs || 0)) >= UPDATE_CHECK_FLOOR_MS;
}

// ---------------------------------------------------------------------------
// R80 — WHEN A `controllerchange` IS WORTH RELOADING FOR, AND IT IS NOT ALWAYS.
//
// **MEASURED, NOT READ: EVERY FIRST-EVER VISIT TO THIS SITE RELOADED ITSELF.** 12 fresh browser
// contexts out of 12, and the page said why in its own words —
// `load:uncontrolled` -> `controllerchange` -> `load:controlled`. `activate` calls
// `clients.claim()` (that is what claiming the open pages MEANS), a page that had no controller
// gets one, `controllerchange` fires, and the latch in `src/lib/pwa.ts` reloaded.
//
// **THAT EVENT CARRIES TWO DIFFERENT FACTS AND THE LATCH WAS COLLAPSING THEM (R18's distinction).**
//   • THE PAGE HAD A CONTROLLER AND IT WAS REPLACED. The document that is on screen was served by
//     the OLD worker; the page is now running old code with a new worker underneath it. **That is
//     what the reload is for**, and it is the only thing it is for.
//   • THE PAGE HAD NO CONTROLLER AND HAS JUST BEEN CLAIMED FOR THE FIRST TIME. Nothing was
//     replaced. The document came straight off the network moments ago and is the newest there is.
//     **Reloading here re-fetches an app she already has, for nothing.**
//
// The latch was never wrong — it stops the INFINITE loop, which is a worse failure. It simply never
// asked whether the FIRST reload was worth doing, and the answer for every new visitor is no.
//
// WHAT IT COSTS HER, WHICH IS WHY THIS IS NOT AN INSTRUMENT FIX: a member landing on 12thhouse.ai
// for the first time has the page pulled out from under her about 130ms in — measured at up to
// 449ms on a loaded machine, which is every phone. Anything typed in that window is gone, and on a
// slow connection it is a visible flash and a second load of the whole app. **She is told nothing,
// and there is no error anywhere**, which is why it survived every gate this repo has.
//
// **THE FACT IS "WAS THIS PAGE CONTROLLED IMMEDIATELY BEFORE THIS EVENT", NOT "AT LOAD", AND THE
// DIFFERENCE IS HER REFRESH BUTTON.** Written as at-load it breaks the one case the reload exists
// for: a member who arrives, is claimed for the first time, STAYS on the page across a deploy and
// then taps Refresh. That is a second `controllerchange` on a page that loaded uncontrolled — and a
// Refresh button that does nothing is the failure `applyUpdate` already refuses to ship one line
// below. So the caller carries the fact forward: the first claim SETS it without reloading, and
// every change after that is a replacement. It has to be read before the event either way, because
// once it fires `navigator.serviceWorker.controller` is already set and the question is unaskable.
//
// Both inputs are read as strict booleans, so a caller that hands over `undefined` (nothing
// registered yet) is answered NO rather than crashing on a member's phone.
// ---------------------------------------------------------------------------
export function shouldReloadOnControllerChange({ wasControlled, alreadyReloading } = {}) {
  if (alreadyReloading === true) return false;   // THE LATCH. Never twice, whatever else is true.
  return wasControlled === true;                 // Replaced -> reload. First claim -> nothing to do.
}

// ---------------------------------------------------------------------------
// THE VISIBLE AFFORDANCE (her fourth piece), and the copy is here because it is a member-facing
// sentence: no machinery talk, no version numbers, no "service worker".
//
// **IT IS NEVER AUTOMATIC.** A worker that calls `skipWaiting()` on install swaps the app out from
// under whoever is mid-sentence in Ask Lala or mid-seal in the Magic Room. She is TOLD, and she
// decides when.
// ---------------------------------------------------------------------------
export const UPDATE_COPY = {
  title: "A new version of the site is ready.",
  action: "Refresh",
  dismiss: "Later",
};
